// BusinessSection — the property card's "Businesses here" facet (BUSINESS_ARCHITECTURE.md §7).
// PROPERTY-FIRST entry is the ONLY add-business flow by design: every Georgian registered
// entity has an address, so a business always starts from a property — there is deliberately
// no standalone add-business button anywhere in the Desk.
//
// Identity-first steps (§7.3): 1) business identity  2) location = THIS property (pre-linked)
// 3) sale listing — optional/skippable, a business exists before the owner commits to selling
// (price null = "ფასი შეთანხმებით", a first-class market mode).

import { useEffect, useState } from 'react'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import { t } from '../lib/i18n.js'
import { getBusinessVocab, getBusinesses, getBusiness, createBusiness, createBusinessOffer, updateBusinessOffer, createBusinessFinancial, setBusinessPublication } from '../db/core.js'
import { Icon } from './Icon.jsx'

const input = 'w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
const btn   = (variant = 'primary') =>
  variant === 'primary'
    ? 'inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50'
    : 'inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted/60 disabled:opacity-50'

// Icon names, not emoji (2026-08-10). Six reuse the property-taxonomy glyphs added the same
// day; `content_cut`, `fitness_center` and `directions_car` were added for the three a real
// estate taxonomy has no word for. ⚠️ `directions_car` is the VEHICLE — `garage` is a
// building, and an auto service is not a parking structure.
const CAT_ICON = {
  food_beverage: 'restaurant', beauty: 'content_cut', retail: 'storefront',
  guesthouse: 'hotel', auto_service: 'directions_car', agriculture: 'agriculture',
  manufacturing: 'factory', warehouse: 'warehouse', fitness: 'fitness_center',
  // core 4165 (2026-11-03): glyphs Icon.jsx already carries. energy, online_business and services have none of
  // their own yet and fall back to `store`, as entertainment does.
  construction: 'roofing', medical: 'local_hospital', education: 'school',
}
// One renderer, so the three call sites cannot drift — and the fallback is `store`, the same
// glyph the rail uses for `commercial`, rather than a second opinion about what a business is.
const CatIcon = ({ k }) => <Icon name={CAT_ICON[k] || 'store'} size={14} />

const money = (n, c) => (n == null ? '—' : `${Number(n).toLocaleString()} ${c || ''}`)

// ── Offers and financial claims (core 3922, owner 2026-10-26) ──────────────────────────
// One business, several offers. ⚠️ Each offer is shown and priced ON ITS OWN line: the
// up-front price and the buyer's recurring rent belong to the same offer, and an $80k
// "business only" line must never borrow the $450k "with premises" price. Do not sum,
// min or merge offers into one number anywhere in this file.
// core 4166: business_for_lease — an operator runs it and pays the owner monthly. Its fee is the buyer_rent_* fields and
// its price the optional payment up front, so for that kind the FEE is the headline figure.
const OFFER_KINDS = ['business_only_premises_leased', 'business_with_premises', 'business_sold_premises_rented_from_seller', 'business_for_lease']
// An offer whose taker pays something monthly — rent for the premises, or the lease fee.
const OFFER_HAS_RENT = k => k !== 'business_with_premises'
const IS_LEASE = k => k === 'business_for_lease'
const FIN_KINDS = ['annual_turnover', 'monthly_revenue', 'daily_revenue', 'net_profit']
// 'reviewed' is deliberately absent: the server refuses it from a non-management caller.
const EVIDENCE_AGENT = ['seller_stated', 'documents_supplied']
const CURRENCIES = ['USD', 'GEL', 'EUR']
const numOrNull = v => (v === '' || v == null || !isFinite(+v) ? null : +v)
const blankOffer = () => ({ offer_kind: 'business_only_premises_leased', share_percent: '', price: '', currency: 'USD', price_on_request: false, buyer_rent_amount: '', buyer_rent_currency: 'USD', buyer_rent_period: 'month' })
// a share is 0 < x < 100 (core 4166); empty or 100 = the whole business, sent as null
const shareOf = o => { const n = IS_LEASE(o.offer_kind) ? null : numOrNull(o.share_percent); return n != null && n > 0 && n < 100 ? n : null }
const blankFin = () => ({ kind: 'annual_turnover', amount_min: '', amount_max: '', currency: 'GEL', period_year: '', evidence_state: 'seller_stated' })

export function offerPayload(o) {
  const rent = OFFER_HAS_RENT(o.offer_kind) ? numOrNull(o.buyer_rent_amount) : null
  return {
    offer_kind: o.offer_kind,
    share_percent: shareOf(o),
    price_on_request: !!o.price_on_request,
    price: o.price_on_request ? null : numOrNull(o.price),
    currency: o.price_on_request ? null : o.currency,
    buyer_rent_amount: rent,
    buyer_rent_currency: rent != null ? o.buyer_rent_currency : null,
    buyer_rent_period: rent != null ? o.buyer_rent_period : null,
    availability_confirmed_at: new Date().toISOString(),
  }
}
export const offerUsable = o => o.price_on_request || numOrNull(IS_LEASE(o.offer_kind) ? o.buyer_rent_amount : o.price) != null
function finPayload(f) {
  return {
    kind: f.kind, amount_min: numOrNull(f.amount_min), amount_max: numOrNull(f.amount_max),
    currency: f.currency, period_year: numOrNull(f.period_year), evidence_state: f.evidence_state,
  }
}
const finUsable = f => numOrNull(f.amount_min) != null

// One offer = one line: what it is, the up-front price, then the buyer's rent if any. A share says its size first
// ("40% share · …"); a business for lease leads with its fee and shows any payment up front under it (core 4166).
export function OfferLine({ o }) {
  const per = t(o.buyer_rent_period === 'year' ? 'desk_biz_per_year' : 'desk_biz_per_month')
  const fee = o.buyer_rent_amount != null ? `${money(o.buyer_rent_amount, o.buyer_rent_currency)} ${per}` : null
  const share = o.share_percent != null && !IS_LEASE(o.offer_kind) ? t('desk_biz_offer_share_line').replace('{pct}', Number(o.share_percent).toLocaleString()) : null
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="text-muted-foreground truncate">
        {share && <span className="font-semibold text-foreground">{share} · </span>}
        {t(`desk_biz_offer_${o.offer_kind}`)}
      </span>
      {IS_LEASE(o.offer_kind) ? (
        <span className="font-semibold tabular-nums shrink-0 text-right">
          {o.price_on_request || fee == null ? t('desk_price_on_request') : fee}
          {!o.price_on_request && o.price != null && (
            <span className="block font-normal text-muted-foreground">
              {t('desk_biz_offer_upfront_line').replace('{amount}', money(o.price, o.currency))}
            </span>
          )}
        </span>
      ) : (
        <span className="font-semibold tabular-nums shrink-0 text-right">
          {o.price_on_request || o.price == null ? t('desk_price_on_request') : money(o.price, o.currency)}
          {fee != null && (
            <span className="block font-normal text-muted-foreground">
              + {t('desk_biz_offer_rent_line').replace('{amount}', money(o.buyer_rent_amount, o.buyer_rent_currency)).replace('{period}', per)}
            </span>
          )}
        </span>
      )}
    </div>
  )
}

// A seller's figure is always labelled with where it stands — never shown as plain fact.
function FinancialLine({ f }) {
  const amt = f.amount_max != null && f.amount_max !== f.amount_min
    ? `${Number(f.amount_min).toLocaleString()}–${money(f.amount_max, f.currency)}`
    : money(f.amount_min, f.currency)
  return (
    <li className="text-xs">
      <span className="text-muted-foreground">{t(`desk_biz_fin_${f.kind}`)}:</span> {amt}
      {f.period_year ? ` (${f.period_year})` : ''}
      <span className="text-muted-foreground"> · {t(`desk_biz_evidence_${f.evidence_state}`)}</span>
      {f.source === 'idmon' && <span className="text-muted-foreground"> · {t('desk_biz_from_advert')}</span>}
    </li>
  )
}

export function OfferEditor({ o, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...o, [k]: v })
  return (
    <div className="grid grid-cols-6 gap-2 rounded-lg border border-border p-2">
      <label className="text-xs text-muted-foreground col-span-6">{t('desk_biz_offer_kind')}
        <select className={input} value={o.offer_kind} onChange={e => set('offer_kind', e.target.value)}>
          {OFFER_KINDS.map(k => <option key={k} value={k}>{t(`desk_biz_offer_${k}`)}</option>)}
        </select>
      </label>
      <label className="text-xs text-muted-foreground col-span-6 flex items-center gap-2">
        <input type="checkbox" checked={o.price_on_request} onChange={e => set('price_on_request', e.target.checked)} />
        {t('desk_price_on_request')}
      </label>
      {!IS_LEASE(o.offer_kind) && (
        <label className="text-xs text-muted-foreground col-span-3">{t('desk_biz_offer_share')}
          <input className={input} type="number" min="1" max="99" placeholder={t('desk_biz_offer_share_whole')} value={o.share_percent ?? ''} onChange={e => set('share_percent', e.target.value)} />
        </label>
      )}
      {!IS_LEASE(o.offer_kind) && <div className="col-span-3" />}
      {!o.price_on_request && (<>
        <label className="text-xs text-muted-foreground col-span-4">{t(IS_LEASE(o.offer_kind) ? 'desk_biz_offer_upfront' : 'desk_biz_price')}
          <input className={input} type="number" value={o.price} onChange={e => set('price', e.target.value)} />
        </label>
        <label className="text-xs text-muted-foreground col-span-2">{t('desk_biz_currency')}
          <select className={input} value={o.currency} onChange={e => set('currency', e.target.value)}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </label>
      </>)}
      {OFFER_HAS_RENT(o.offer_kind) && (<>
        <label className="text-xs text-muted-foreground col-span-3">{t(IS_LEASE(o.offer_kind) ? 'desk_biz_offer_fee' : 'desk_biz_offer_buyer_rent')}
          <input className={input} type="number" value={o.buyer_rent_amount} onChange={e => set('buyer_rent_amount', e.target.value)} />
        </label>
        <label className="text-xs text-muted-foreground col-span-1">&nbsp;
          <select className={input} value={o.buyer_rent_currency} onChange={e => set('buyer_rent_currency', e.target.value)}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-xs text-muted-foreground col-span-2">&nbsp;
          <select className={input} value={o.buyer_rent_period} onChange={e => set('buyer_rent_period', e.target.value)}>
            <option value="month">{t('desk_biz_per_month')}</option>
            <option value="year">{t('desk_biz_per_year')}</option>
          </select>
        </label>
      </>)}
      {onRemove && <div className="col-span-6 flex justify-end"><button type="button" className={btn('ghost')} onClick={onRemove}>✕</button></div>}
    </div>
  )
}

function FinancialEditor({ f, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...f, [k]: v })
  return (
    <div className="grid grid-cols-6 gap-2 rounded-lg border border-border p-2">
      <label className="text-xs text-muted-foreground col-span-3">{t('desk_biz_fin_kind')}
        <select className={input} value={f.kind} onChange={e => set('kind', e.target.value)}>
          {FIN_KINDS.map(k => <option key={k} value={k}>{t(`desk_biz_fin_${k}`)}</option>)}
        </select>
      </label>
      <label className="text-xs text-muted-foreground col-span-3">{t('desk_biz_fin_evidence')}
        <select className={input} value={f.evidence_state} onChange={e => set('evidence_state', e.target.value)}>
          {EVIDENCE_AGENT.map(k => <option key={k} value={k}>{t(`desk_biz_evidence_${k}`)}</option>)}
        </select>
      </label>
      <label className="text-xs text-muted-foreground col-span-2">{t('desk_biz_fin_amount_min')}
        <input className={input} type="number" value={f.amount_min} onChange={e => set('amount_min', e.target.value)} />
      </label>
      <label className="text-xs text-muted-foreground col-span-2">{t('desk_biz_fin_amount_max')}
        <input className={input} type="number" value={f.amount_max} onChange={e => set('amount_max', e.target.value)} />
      </label>
      <label className="text-xs text-muted-foreground col-span-1">{t('desk_biz_currency')}
        <select className={input} value={f.currency} onChange={e => set('currency', e.target.value)}>
          {CURRENCIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </label>
      <label className="text-xs text-muted-foreground col-span-1">{t('desk_biz_fin_year')}
        <input className={input} type="number" value={f.period_year} onChange={e => set('period_year', e.target.value)} />
      </label>
      {onRemove && <div className="col-span-6 flex justify-end"><button type="button" className={btn('ghost')} onClick={onRemove}>✕</button></div>}
    </div>
  )
}

// ── Business feed (Desk type filter → 'Business') ────────────────────────────
// Exclusive feed like PoolFeed: businesses are findable HERE regardless of whether their
// host property has a listing — business-intake-created property rows have no listing, so
// no property card ever renders for them and the "Businesses here" facet is unreachable
// (the Rukhi case, 2026-07-11). Read-only v1: card grid + detail modal.

const catLabelOf = c => (c ? (c.label_ka || c.label_en || c.key) : '')

function claimValue(cl) {
  if (cl.value_num != null) return Number(cl.value_num).toLocaleString()
  if (cl.value_text != null) return cl.value_text
  if (cl.value_json != null) return JSON.stringify(cl.value_json)
  return '—'
}

function BusinessDetailModal({ businessId, summary, onClose }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const [d, setD] = useState(null)
  const [err, setErr] = useState(null)
  const load = () => getBusiness(businessId).then(setD).catch(e => setErr(e.message))
  useEffect(() => { load() }, [businessId])
  const [newOffer, setNewOffer] = useState(null)
  const [newFin, setNewFin] = useState(null)
  const [busy, setBusy] = useState(false)
  async function act(fn) {
    setBusy(true); setErr(null)
    try { await fn(); await load() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const saveOffer = () => act(async () => { await createBusinessOffer(businessId, offerPayload(newOffer)); setNewOffer(null) })
  const saveFin = () => act(async () => { await createBusinessFinancial(businessId, finPayload(newFin)); setNewFin(null) })
  const withdrawOffer = id => act(() => updateBusinessOffer(id, { status: 'withdrawn' }))
  const confirmOffer = id => act(() => updateBusinessOffer(id, { availability_confirmed_at: new Date().toISOString() }))
  const activeOffers = (d?.offers || []).filter(o => o.status === 'active')
  const pub = d?.business?.meta?.publication || {}
  const setPub = patch => act(() => setBusinessPublication(businessId, {
    isPublic: pub.public, showName: pub.show_name, showLocation: pub.show_location, ...patch,
  }))

  const b = d?.business
  const activeListing = (d?.listings || []).find(l => l.status === 'active') || null
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-2xl shadow-xl my-8">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-semibold truncate">
              <CatIcon k={summary?.category_key || b?.category_key} /> {summary?.name || b?.name || catLabelOf(summary?.category)}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {catLabelOf(summary?.category)}{b?.status && b.status !== 'active' ? ` · ${b.status}` : ''}
            </p>
          </div>
          <button onClick={onClose} className={btn('ghost')}>✕</button>
        </div>
        <div className="px-5 py-3 space-y-3 text-sm">
          {err && <p className="text-xs text-red-500"><Icon name="alert-triangle" /> {err}</p>}
          {!d && !err && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}
          {d && (<>
            {b?.description && <p className="text-xs whitespace-pre-wrap">{b.description}</p>}

            {/* sentavus.com — nothing is public until ticked; name and exact address stay hidden unless ticked too */}
            <div className="rounded-lg border border-border px-3 py-2 space-y-1">
              <label className="text-xs flex items-center gap-2">
                <input type="checkbox" disabled={busy} checked={!!pub.public} onChange={e => setPub({ isPublic: e.target.checked })} />
                {t('desk_biz_pub_public')}
              </label>
              {pub.public && (<>
                <label className="text-xs flex items-center gap-2 pl-5">
                  <input type="checkbox" disabled={busy} checked={!!pub.show_name} onChange={e => setPub({ showName: e.target.checked })} />
                  {t('desk_biz_pub_show_name')}
                </label>
                <label className="text-xs flex items-center gap-2 pl-5">
                  <input type="checkbox" disabled={busy} checked={!!pub.show_location} onChange={e => setPub({ showLocation: e.target.checked })} />
                  {t('desk_biz_pub_show_location')}
                </label>
                {activeOffers.length === 0 && <p className="text-[11px] text-muted-foreground pl-5">{t('desk_biz_pub_needs_offer')}</p>}
              </>)}
            </div>

            {(b?.trading_status || b?.handover_support) && (
              <p className="text-xs text-muted-foreground">
                {b.trading_status ? t(`desk_biz_trading_${b.trading_status}`) : ''}
                {b.handover_support ? `${b.trading_status ? ' · ' : ''}${t('desk_biz_handover')}${b.handover_weeks ? ` — ${b.handover_weeks} ${t('desk_biz_weeks')}` : ''}` : ''}
              </p>
            )}

            {/* offers — one line each; negotiable is a first-class mode, never "missing" */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_biz_offers')}</p>
              {activeOffers.length === 0 && !activeListing && <p className="text-xs text-muted-foreground">{t('desk_biz_no_offers')}</p>}
              {activeOffers.length === 0 && activeListing && (
                <p className="text-sm font-semibold">{activeListing.price != null ? money(activeListing.price, activeListing.currency) : t('desk_price_on_request')}</p>
              )}
              <div className="space-y-2">
                {activeOffers.map(o => (
                  <div key={o.id} className="rounded-lg border border-border px-2 py-1.5">
                    <OfferLine o={o} />
                    <div className="flex items-center justify-between gap-2 mt-1 text-[11px] text-muted-foreground">
                      <span>
                        {o.availability_confirmed_at ? t('desk_biz_offer_confirmed').replace('{date}', new Date(o.availability_confirmed_at).toLocaleDateString()) : t('desk_biz_offer_unconfirmed')}
                        {o.source === 'idmon' ? ` · ${t('desk_biz_from_advert')}` : ''}
                      </span>
                      <span className="flex gap-2 shrink-0">
                        <button type="button" className="underline disabled:opacity-50" disabled={busy} onClick={() => confirmOffer(o.id)}>{t('desk_biz_offer_confirm')}</button>
                        <button type="button" className="underline disabled:opacity-50" disabled={busy} onClick={() => withdrawOffer(o.id)}>{t('desk_biz_offer_withdraw')}</button>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {newOffer ? (
                <div className="mt-2 space-y-2">
                  <OfferEditor o={newOffer} onChange={setNewOffer} />
                  <div className="flex gap-2">
                    <button className={btn('primary')} disabled={busy || !offerUsable(newOffer)} onClick={saveOffer}>{t('desk_biz_save')}</button>
                    <button className={btn('ghost')} disabled={busy} onClick={() => setNewOffer(null)}>{t('desk_biz_cancel')}</button>
                  </div>
                </div>
              ) : (
                <button type="button" className={`${btn('ghost')} mt-2`} onClick={() => setNewOffer(blankOffer())}>＋ {t('desk_biz_offer_add')}</button>
              )}
            </div>

            {(d.locations || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_biz_locations')}</p>
                <ul className="text-xs space-y-1">
                  {d.locations.map((o, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span>{o.tenure === 'owned' ? t('desk_biz_tenure_owned') : t('desk_biz_tenure_leased')}{o.is_primary ? ' ★' : ''}</span>
                      <span className="text-muted-foreground truncate">{o.property?.address_text || o.property_id}</span>
                      {o.property?.cadastral_code && <span className="text-muted-foreground tabular-nums shrink-0">{o.property.cadastral_code}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(d.features || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_biz_features')}</p>
                <ul className="text-xs space-y-0.5">
                  {d.features.map((f, i) => (
                    <li key={i}><span className="text-muted-foreground">{f.definition?.label_ka || f.definition?.label_en || f.definition?.key}:</span> {String(f.value)}</li>
                  ))}
                </ul>
              </div>
            )}

            {(d.assets || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_biz_assets')}</p>
                <ul className="text-xs space-y-0.5">
                  {d.assets.map(a => <li key={a.id}>{a.label || a.kind_key}{a.quantity != null && a.quantity !== 1 ? ` × ${a.quantity}` : ''}</li>)}
                </ul>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_biz_financials')}</p>
              {(d.financials || []).length > 0 && <ul className="space-y-0.5">{d.financials.map(f => <FinancialLine key={f.id} f={f} />)}</ul>}
              {newFin ? (
                <div className="mt-2 space-y-2">
                  <FinancialEditor f={newFin} onChange={setNewFin} />
                  <div className="flex gap-2">
                    <button className={btn('primary')} disabled={busy || !finUsable(newFin)} onClick={saveFin}>{t('desk_biz_save')}</button>
                    <button className={btn('ghost')} disabled={busy} onClick={() => setNewFin(null)}>{t('desk_biz_cancel')}</button>
                  </div>
                </div>
              ) : (
                <button type="button" className={`${btn('ghost')} mt-2`} onClick={() => setNewFin(blankFin())}>＋ {t('desk_biz_fin_add')}</button>
              )}
            </div>

            {(d.claims || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">{t('desk_biz_claims')}</p>
                <ul className="text-xs space-y-0.5">
                  {d.claims.map((cl, i) => (
                    <li key={i}>
                      <span className="text-muted-foreground">{cl.attribute.replace(/_/g, ' ')}:</span> {claimValue(cl)}
                      {cl.source_kind && <span className="text-muted-foreground"> · {cl.source_kind}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  )
}

export function BusinessFeed({ query }) {
  const [businesses, setBusinesses] = useState(null)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState(null)   // business summary row
  useEffect(() => { getBusinesses().then(d => setBusinesses(d.businesses || [])).catch(e => setErr(e.message)) }, [])
  // Dead-box stopgap (search blueprint §11, step 8): the Desk search box used to look
  // functional with Business selected but filtered nothing. Rows live in memory → cheap
  // client-side substring filter over name/category/address (option b, same-script only).
  const q = (query || '').trim().toLowerCase()
  const shown = q ? (businesses || []).filter(b =>
    (b.name || '').toLowerCase().includes(q)
    || (b.category_key || '').toLowerCase().includes(q)
    || (b.primary_location?.property?.address_text || '').toLowerCase().includes(q)) : businesses
  return (
    <div>
      {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      {!businesses && !err && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}
      {businesses?.length === 0 && <p className="text-sm text-muted-foreground">{t('desk_biz_feed_empty')}</p>}
      {businesses?.length > 0 && shown?.length === 0 && <p className="text-sm text-muted-foreground">{t('desk_biz_feed_empty')}</p>}
      <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]">
        {(shown || []).map(b => {
          const al = b.active_listing
          const loc = b.primary_location?.property
          return (
            <div key={b.id} className="bg-card border border-border rounded-lg p-3 cursor-pointer hover:bg-accent/50"
              onClick={() => setOpen(b)}>
              <div className="text-sm font-medium truncate">
                <CatIcon k={b.category_key} /> {b.name || catLabelOf(b.category) || b.category_key}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                {catLabelOf(b.category)}{loc?.address_text ? ` · ${loc.address_text}` : ''}
              </div>
              {(b.offers || []).length > 0 ? (
                <div className="mt-1 space-y-1">{b.offers.map(o => <OfferLine key={o.id} o={o} />)}</div>
              ) : (
                <div className="text-xs font-semibold mt-1">
                  {al
                    ? (al.price != null ? money(al.price, al.currency) : t('desk_price_on_request'))
                    : <span className="font-normal text-muted-foreground">{t('desk_biz_no_offers')}</span>}
                </div>
              )}
              {b.status !== 'active' && <div className="text-xs text-muted-foreground mt-0.5">{b.status}</div>}
            </div>
          )
        })}
      </div>
      {open && <BusinessDetailModal businessId={open.id} summary={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

export default function BusinessSection({ propertyId }) {
  const [businesses, setBusinesses] = useState(null)   // null = loading
  const [vocab, setVocab] = useState(null)
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [form, setForm] = useState({
    category_key: '', name: '', description: '', legal_form: '', tenure: 'leased',
    monthly_rent: '', landlord_consent: '', land_status: '', employee_full_time: '',
    reason_for_sale: '', government_grant_obligation: false, grant_note: '',
    relationship_type: 'hq', trading_status: '', handover_support: false, handover_weeks: '',
    offers: [blankOffer()], financials: [],
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const reload = () => getBusinesses({ propertyId }).then(d => setBusinesses(d.businesses || [])).catch(() => setBusinesses([]))
  useEffect(() => { if (propertyId) reload() }, [propertyId])
  useEffect(() => { if (adding && !vocab) getBusinessVocab().then(setVocab).catch(e => setErr(e.message)) }, [adding])

  if (!propertyId) return null

  const catLabel = c => (c ? (c.label_ka || c.label_en) : '')

  async function handleSave() {
    if (!form.category_key) { setErr(t('desk_biz_err_category')); return }
    setSaving(true); setErr(null)
    try {
      const payload = {
        category_key: form.category_key,
        name: form.name.trim() || null,
        description: form.description.trim() || null,
        legal_form: form.legal_form || null,
        employee_full_time: numOrNull(form.employee_full_time),
        reason_for_sale: form.reason_for_sale || null,
        government_grant_obligation: form.government_grant_obligation,
        grant_note: form.government_grant_obligation ? (form.grant_note.trim() || null) : null,
        trading_status: form.trading_status || null,
        handover_support: form.handover_support,
        handover_weeks: form.handover_support ? numOrNull(form.handover_weeks) : null,
        // Offers replace the old single "sale listing" step: each carries its own price.
        offers: form.offers.filter(offerUsable).map(offerPayload),
        financials: form.financials.filter(finUsable).map(finPayload),
        properties: [{
          property_id: propertyId,
          tenure: form.tenure,
          relationship_type: form.relationship_type,
          is_primary: true,
          monthly_rent: form.tenure === 'leased' ? numOrNull(form.monthly_rent) : null,
          rent_currency: form.tenure === 'leased' && form.monthly_rent ? 'USD' : null,
          landlord_consent: form.tenure === 'leased' ? (form.landlord_consent || null) : null,
          land_status: form.land_status || null,
        }],
      }
      await createBusiness(payload)
      setAdding(false)
      setForm(f => ({ ...f, category_key: '', name: '', description: '', monthly_rent: '', grant_note: '', offers: [blankOffer()], financials: [] }))
      await reload()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  // Land status matters whenever the premises are a plot — not only for agriculture
  // (owner 2026-10-14: businesses on land, e.g. a car wash or campsite on a plot).
  const isAgro = form.category_key === 'agriculture' || form.relationship_type === 'operating_land'
  const cats = vocab?.categories || []

  // Rendered INSIDE the property modal's Facet (IntakeTab provides the collapsible
  // title bar) — so no outer card/header of our own, just the content.
  return (
    <div>
      {!adding && (
        <div className="flex justify-end mb-2">
          <button className={btn('ghost')} onClick={() => setAdding(true)}>＋ {t('desk_biz_add')}</button>
        </div>
      )}

      {/* existing businesses at this property */}
      {businesses === null ? (
        <p className="text-xs text-muted-foreground">…</p>
      ) : businesses.length === 0 && !adding ? (
        <p className="text-xs text-muted-foreground">{t('desk_biz_none')}</p>
      ) : (
        <div className="space-y-2">
          {businesses.map(b => {
            const al = b.active_listing
            return (
              <div key={b.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    <CatIcon k={b.category_key} /> {b.name || catLabel(b.category) || b.category_key}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {catLabel(b.category)}{b.status !== 'active' ? ` · ${b.status}` : ''}
                  </p>
                </div>
                <div className="shrink-0 ml-2 min-w-[45%]">
                  {(b.offers || []).length > 0 ? (
                    <div className="space-y-1">{b.offers.map(o => <OfferLine key={o.id} o={o} />)}</div>
                  ) : al ? (
                    <span className="block text-right text-xs font-semibold">
                      {al.price != null ? `${al.price.toLocaleString()} ${al.currency}` : t('desk_price_on_request')}
                    </span>
                  ) : (
                    <span className="block text-right text-xs text-muted-foreground">{t('desk_biz_no_offers')}</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* add form — identity → location(pre-linked) → optional listing */}
      {adding && (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-muted-foreground">{t('desk_biz_category')}
              <select className={input} value={form.category_key} onChange={e => set('category_key', e.target.value)}>
                <option value="">—</option>
                {cats.map(c => <option key={c.key} value={c.key}>{CAT_ICON[c.key] || ''} {catLabel(c)}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_biz_name')}
              <input className={input} value={form.name} onChange={e => set('name', e.target.value)} />
            </label>
            <label className="text-xs text-muted-foreground col-span-2">{t('desk_biz_description')}
              <textarea className={input} rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_biz_legal_form')}
              <select className={input} value={form.legal_form} onChange={e => set('legal_form', e.target.value)}>
                <option value="">—</option>
                <option value="llc">შპს (LLC)</option>
                <option value="ie">ინდ. მეწარმე (IE)</option>
                <option value="other">{t('desk_biz_other')}</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_biz_tenure')}
              <select className={input} value={form.tenure} onChange={e => set('tenure', e.target.value)}>
                <option value="leased">{t('desk_biz_tenure_leased')}</option>
                <option value="owned">{t('desk_biz_tenure_owned')}</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_biz_relationship')}
              <select className={input} value={form.relationship_type} onChange={e => set('relationship_type', e.target.value)}>
                <option value="hq">{t('desk_biz_rel_hq')}</option>
                <option value="operating_land">{t('desk_biz_rel_operating_land')}</option>
                <option value="branch">{t('desk_biz_rel_branch')}</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_biz_trading')}
              <select className={input} value={form.trading_status} onChange={e => set('trading_status', e.target.value)}>
                <option value="">—</option>
                <option value="trading">{t('desk_biz_trading_trading')}</option>
                <option value="temporarily_closed">{t('desk_biz_trading_temporarily_closed')}</option>
                <option value="assets_only">{t('desk_biz_trading_assets_only')}</option>
              </select>
            </label>
            {form.tenure === 'leased' && (<>
              <label className="text-xs text-muted-foreground">{t('desk_biz_rent')}
                <input className={input} type="number" value={form.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} />
              </label>
              <label className="text-xs text-muted-foreground">{t('desk_biz_landlord_consent')}
                <select className={input} value={form.landlord_consent} onChange={e => set('landlord_consent', e.target.value)}>
                  <option value="">—</option>
                  <option value="yes">{t('desk_biz_yes')}</option>
                  <option value="pending">{t('desk_biz_pending')}</option>
                  <option value="no">{t('desk_biz_no')}</option>
                </select>
              </label>
            </>)}
            {isAgro && (
              <label className="text-xs text-muted-foreground">{t('desk_biz_land_status')}
                <select className={input} value={form.land_status} onChange={e => set('land_status', e.target.value)}>
                  <option value="">—</option>
                  <option value="agricultural">{t('desk_biz_land_agricultural')}</option>
                  <option value="non_agricultural">{t('desk_biz_land_non_agricultural')}</option>
                </select>
              </label>
            )}
            <label className="text-xs text-muted-foreground">{t('desk_biz_employees')}
              <input className={input} type="number" value={form.employee_full_time} onChange={e => set('employee_full_time', e.target.value)} />
            </label>
            <label className="text-xs text-muted-foreground">{t('desk_biz_reason')}
              <select className={input} value={form.reason_for_sale} onChange={e => set('reason_for_sale', e.target.value)}>
                <option value="">—</option>
                <option value="retirement">{t('desk_biz_reason_retirement')}</option>
                <option value="relocation">{t('desk_biz_reason_relocation')}</option>
                <option value="partnership_dissolution">{t('desk_biz_reason_partnership')}</option>
                <option value="expansion">{t('desk_biz_reason_expansion')}</option>
                <option value="health">{t('desk_biz_reason_health')}</option>
                <option value="lifestyle">{t('desk_biz_reason_lifestyle')}</option>
                <option value="other">{t('desk_biz_other')}</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground col-span-2 flex items-center gap-2 mt-1">
              <input type="checkbox" checked={form.government_grant_obligation}
                onChange={e => set('government_grant_obligation', e.target.checked)} />
              {t('desk_biz_grant')}
            </label>
            {form.government_grant_obligation && (
              <label className="text-xs text-muted-foreground col-span-2">{t('desk_biz_grant_note')}
                <input className={input} value={form.grant_note} onChange={e => set('grant_note', e.target.value)} />
              </label>
            )}
            <label className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
              <input type="checkbox" checked={form.handover_support} onChange={e => set('handover_support', e.target.checked)} />
              {t('desk_biz_handover')}
            </label>
            {form.handover_support && (
              <label className="text-xs text-muted-foreground">{t('desk_biz_handover_weeks')}
                <input className={input} type="number" value={form.handover_weeks} onChange={e => set('handover_weeks', e.target.value)} />
              </label>
            )}
          </div>

          {/* offers — zero or more; a business can be recorded before anything is offered */}
          <div className="border-t border-border pt-2 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">{t('desk_biz_offers')}</p>
            {form.offers.map((o, i) => (
              <OfferEditor key={i} o={o}
                onChange={v => set('offers', form.offers.map((x, j) => (j === i ? v : x)))}
                onRemove={() => set('offers', form.offers.filter((_, j) => j !== i))} />
            ))}
            <button type="button" className={btn('ghost')} onClick={() => set('offers', [...form.offers, blankOffer()])}>＋ {t('desk_biz_offer_add')}</button>
          </div>

          <div className="border-t border-border pt-2 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">{t('desk_biz_financials')}</p>
            {form.financials.map((f, i) => (
              <FinancialEditor key={i} f={f}
                onChange={v => set('financials', form.financials.map((x, j) => (j === i ? v : x)))}
                onRemove={() => set('financials', form.financials.filter((_, j) => j !== i))} />
            ))}
            <button type="button" className={btn('ghost')} onClick={() => set('financials', [...form.financials, blankFin()])}>＋ {t('desk_biz_fin_add')}</button>
          </div>

          {err && <p className="text-xs text-red-500">{err}</p>}
          <div className="flex gap-2">
            <button className={btn('primary')} disabled={saving} onClick={handleSave}>
              {saving ? '…' : t('desk_biz_save')}
            </button>
            <button className={btn('ghost')} disabled={saving} onClick={() => { setAdding(false); setErr(null) }}>
              {t('desk_biz_cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
